/**
 * Logger básico mejorado
 *
 * - En desarrollo: console output con emoji prefixes + almacena últimas 100 entradas.
 * - En producción: errors/warns se envían a Sentry como breadcrumbs automáticamente.
 *   Si el dato es un Error, se captura como excepción en Sentry.
 */

import { capturarExcepcion, dejarMiga } from '@/lib/sentryPuente';

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: string;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  /** Solo con `VITE_ENABLE_VERBOSE_LOGS=true`: info/debug van a console.warn (más visibles). */
  private enableVerbose =
    String(import.meta.env.VITE_ENABLE_VERBOSE_LOGS ?? '').toLowerCase() === 'true';
  private logs: LogEntry[] = [];

  private formatMessage(level: LogLevel, message: string, _data?: unknown): string {
    const timestamp = new Date().toISOString();
    const prefix = this.getLevelPrefix(level);
    return `${prefix} [${timestamp}] ${message}`;
  }

  private getLevelPrefix(level: LogLevel): string {
    const prefixes = {
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      debug: '🔍',
    };
    return prefixes[level];
  }

  private log(level: LogLevel, message: string, data?: unknown) {
    const formattedMessage = this.formatMessage(level, message, data);
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date().toISOString(),
    };

    // Guardar en memoria (útil para debugging)
    if (this.isDevelopment) {
      this.logs.push(entry);
      // Limitar a últimas 100 entradas para evitar memoria excesiva
      if (this.logs.length > 100) {
        this.logs.shift();
      }
    }

    // En desarrollo: warn/error siempre visibles; info/debug en console.debug salvo modo explícito.
    if (import.meta.env.DEV) {
      switch (level) {
        case 'info':
          if (this.enableVerbose) console.warn(formattedMessage, data || '');
          else console.debug(formattedMessage, data || '');
          break;
        case 'warn':
          console.warn(formattedMessage, data || '');
          break;
        case 'error':
          console.error(formattedMessage, data || '');
          break;
        case 'debug':
          if (this.enableVerbose) console.warn(formattedMessage, data || '');
          else console.debug(formattedMessage, data || '');
          break;
      }
    }

    // En producción: errors y warns → Sentry breadcrumbs (no-op si Sentry no inicializado).
    if (!this.isDevelopment) {
      if (level === 'error') {
        dejarMiga('app.error', message, 'error');
        if (data instanceof Error) {
          capturarExcepcion(data, { logMessage: message });
        }
      } else if (level === 'warn') {
        dejarMiga('app.warn', message, 'warning');
      }
    }
  }

  info(message: string, data?: unknown) {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown) {
    this.log('warn', message, data);
  }

  error(message: string, error?: unknown) {
    this.log('error', message, error);
  }

  debug(message: string, data?: unknown) {
    this.log('debug', message, data);
  }

  // Método para obtener logs (útil en desarrollo)
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Método para limpiar logs
  clearLogs() {
    this.logs = [];
  }

  // Método para exportar logs (útil para debugging)
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

const logger = new Logger();

export const logInfo = (message: string, data?: unknown) => logger.info(message, data);
export const logWarn = (message: string, data?: unknown) => logger.warn(message, data);
export const logError = (message: string, error?: unknown) => logger.error(message, error);
export const logDebug = (message: string, data?: unknown) => logger.debug(message, data);
